"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const locationOptions = [
{
  icon: "MapIcon",
  title: "Location history",
  desc: "Review where your pet has been over the last 30 days with a simple activity log.",
  tag: "30-day history",
  tagColor: "#5EBFB3"
},
{
  icon: "BellAlertIcon",
  title: "Safe zone alerts",
  desc: "Set a home perimeter. Get an instant notification if your pet leaves the safe zone.",
  tag: "Instant alerts",
  tagColor: "#7C3AED"
},
{
  icon: "PencilSquareIcon",
  title: "Manual check-ins",
  desc: "Log your pet\'s location manually when you\'re out and about — no special device needed.",
  tag: "No device needed",
  tagColor: "#D97706"
},
{
  icon: "UsersIcon",
  title: "Share with your circle",
  desc: "Let trusted family members, sitters, or walkers see your pet's check-in history.",
  tag: "Trusted sharing",
  tagColor: "#0369A1"
}];


export default function TrackingFeature() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 90);
            });
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 dark:bg-[#0f172a]"
      style={{ background: "linear-gradient(160deg, #F0FAFB 0%, #F7F8F6 100%)" }}>
      
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Copy */}
          <div className="flex flex-col gap-6 scroll-fade-left">
            <span className="teal-badge">Location & Check-ins</span>
            <h2 className="text-4xl sm:text-[42px] font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight leading-tight">
              Stay connected to
              <br />
              <span className="gradient-text">your pet's day.</span>
            </h2>
            <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-lg">
              Log check-ins, set safe zones, and share your pet's activity with the people who care for them — all without any special device.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {locationOptions.map((opt, idx) =>
              <div key={opt.title} className={`scroll-fade-up stagger-${idx + 1} feature-card p-5`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-9 h-9 rounded-xl bg-[#E8F7F6] flex items-center justify-center">
                      <Icon name={opt.icon as "MapIcon"} size={16} className="text-teal-500" />
                    </div>
                    <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: `${opt.tagColor}18`, color: opt.tagColor }}>
                    
                      {opt.tag}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 mb-1 font-heading">{opt.title}</h3>
                  <p className="text-xs text-[#4A6363] dark:text-slate-400 leading-relaxed">{opt.desc}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Image visual */}
          <div className="relative scroll-fade-right">
            <div className="rounded-3xl overflow-hidden" style={{ boxShadow: "0 24px 64px rgba(14,165,160,0.18)" }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1f3a65d23-1772543716038.png"
                alt="Person walking a dog in a park while using the DogFob app to log a check-in"
                width={600}
                height={460}
                className="w-full h-[420px] object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2027]/20 to-transparent" />
            </div>

            {/* Check-in overlay */}
            <div
              className="absolute top-5 right-5 glass-card rounded-2xl p-4 animate-float-a"
              style={{ boxShadow: "0 12px 36px rgba(15,32,39,0.12)" }}>
              
              <div className="flex items-center gap-2 mb-2">
                <div className="status-dot" />
                <span className="text-xs font-bold text-[#22C55E]">Check-in logged</span>
              </div>
              <p className="text-xs text-[#8AA4A4] dark:text-slate-500">Luna is at</p>
              <p className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">Riverside Park</p>
              <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">Updated 22 sec ago</p>
            </div>

            {/* Safe zone badge */}
            <div
              className="absolute -bottom-4 left-6 glass-card rounded-xl px-4 py-2.5 animate-float-b"
              style={{ boxShadow: "0 8px 28px rgba(15,32,39,0.1)" }}>
              
              <div className="flex items-center gap-2">
                <Icon name="HomeIcon" size={14} className="text-teal-500" />
                <span className="text-xs font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">Safe zone active</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#16A34A] font-bold">ON</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}